import CloseIcon from "@mui/icons-material/Close";
import { LoadingButton } from "@mui/lab";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  GetDetails,
  GetToken,
  signUpAgent,
  signUpClient,
  signUpIndustryProf,
  switchToSignin,
  switchToSignup,
  toggleAuthModal,
} from "../../actions";
import { GET_TOKEN } from "../../actions/types";
import base from "../../apis";
import { URL } from "../../constants";
import { ReactComponent as Bars } from "../../assets/bars.svg";
import EMAIL from "../../assets/email_verify.png";
import Logo from "../../images/BPOTechLogo.svg";
import AuthContent from "../AuthContent/AuthContent";
import Modal from "../Modal/Modal";
import Spinner from "../Spinner/Spinner";
import UserMenuOptions from "../UserMenuOptions/UserMenuOptions";
// import { useDarkMode } from "../../hooks/useDarkMode";
import "./navbar.scss";

const indProfLink = `https://${process.env.REACT_APP_URL.includes("dev") ? "testing." : ""
  }industryprofessionals.com`;

function Navbar(props) {
  const { pathname } = useLocation();
  const isModalOpen = useSelector((state) => state.modals.authModal.isOpen);
  const [menuState, setMenuState] = useState(false);
  const location = useLocation();
  const isVerified = JSON.parse(
    localStorage.getItem("details"),
  )?.is_email_verified;
  const [accountDetails, setAccountDetails] = useState({
    email: "",
    password: "",
    passwordConfirmation: "",
    isProfessional: false,
    professionType: null,
    first_name: "",
    last_name: "",
    zip: "",
    phone: "",
    subType: "",
    marketer_id: "",
    prof_role: "agent",
    is_consent_to_receive_appointment_reminders: false,
    is_consent_to_receive_marketing_emails: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [loginTotpChallenge, setLoginTotpChallenge] = useState({
    required: false,
    useRecoveryCode: false,
    code: "",
    isSubmitting: false,
  });

  React.useLayoutEffect(() => {
    const navbar = document.getElementsByClassName("navbar")[0];
    document.documentElement.style.setProperty(
      "--navheight",
      navbar.clientHeight + "px",
    );
  }, []);

  const { first_name, last_name, loading, id, email, token } = useSelector(
    (state) => state.auth,
  );

  const toggleMenu = (menuState) => {
    setMenuState(!menuState);
  };

  const isSignIn = useSelector((state) => state.modals.authModal.isSignin);

  const dispatch = useDispatch();

  const openLogin = () => {
    dispatch(toggleAuthModal());
    dispatch(switchToSignin());
  };
  const openNewAccount = () => {
    dispatch(toggleAuthModal());
    dispatch(switchToSignup());
  };

  const navigate = useNavigate();

  const onSubmit = () => {
    if (isSignIn) {
      if (accountDetails.email === "" || accountDetails.password === "") {
        toast.warn("Please fill in all fields");
        return;
      }
      const { email, password } = accountDetails;
      dispatch({ type: "START_LOADING" });
      dispatch(
        GetToken(
          { email, password },
          navigate,
          location?.state,
          () =>
            setLoginTotpChallenge({
              required: true,
              useRecoveryCode: false,
              code: "",
              isSubmitting: false,
            }),
        ),
      );
    } else {
      if (accountDetails.email === "" || accountDetails.password === "") {
        toast.warn("Please fill in email and password fields");
        return;
      }
      if (accountDetails.password !== accountDetails.passwordConfirmation) {
        toast.warn("Passwords do not match");
        return;
      }

      if (!accountDetails.isProfessional) {
        dispatch(signUpClient(accountDetails, navigate, location?.state));
      } else {
        if (accountDetails.prof_role === "agent") {
          dispatch(signUpAgent(accountDetails, navigate, location?.state));
        } else {
          dispatch(
            signUpIndustryProf(accountDetails, navigate, location?.state),
          );
        }
      }
    }
  };

  const closeAuthFlow = () => {
    localStorage.removeItem("pending_atoken");
    localStorage.removeItem("pending_rtoken");
    setLoginTotpChallenge({
      required: false,
      useRecoveryCode: false,
      code: "",
      isSubmitting: false,
    });
    dispatch(toggleAuthModal());
  };

  const verifyLoginTotp = async () => {
    const code = loginTotpChallenge.useRecoveryCode
      ? loginTotpChallenge.code.trim()
      : loginTotpChallenge.code.replace(/\D/g, "").slice(0, 6);
    const pendingAccessToken = localStorage.getItem("pending_atoken");
    const pendingRefreshToken = localStorage.getItem("pending_rtoken");

    if (
      (!loginTotpChallenge.useRecoveryCode && code.length < 6) ||
      (loginTotpChallenge.useRecoveryCode && !code)
    ) {
      toast.warn(
        loginTotpChallenge.useRecoveryCode
          ? "Enter a recovery code"
          : "Enter the 6-digit authenticator code"
      );
      return;
    }

    if (!pendingAccessToken || !pendingRefreshToken) {
      toast.error("Your login session expired. Please sign in again.");
      setLoginTotpChallenge({
        required: false,
        useRecoveryCode: false,
        code: "",
        isSubmitting: false,
      });
      return;
    }

    try {
      setLoginTotpChallenge((prev) => ({
        ...prev,
        isSubmitting: true,
      }));

      const { data } = await axios.post(
        `${URL}core/verify-login-totp/`,
        loginTotpChallenge.useRecoveryCode
          ? {
            code,
            recovery_code: code,
            use_recovery_code: true,
          }
          : { code },
        {
          headers: {
            Authorization: `JWT ${pendingAccessToken}`,
          },
        },
      );

      const verifiedAccessToken = data?.access_token || data?.access;
      const verifiedRefreshToken = data?.refresh_token || data?.refresh;

      if (!verifiedAccessToken || !verifiedRefreshToken) {
        throw new Error("Missing verified login tokens.");
      }

      localStorage.setItem("atoken", verifiedAccessToken);
      localStorage.setItem("rtoken", verifiedRefreshToken);
      localStorage.removeItem("pending_atoken");
      localStorage.removeItem("pending_rtoken");
      dispatch({
        type: GET_TOKEN,
        payload: {
          access: verifiedAccessToken,
          refresh: verifiedRefreshToken,
        },
      });
      setLoginTotpChallenge({
        required: false,
        useRecoveryCode: false,
        code: "",
        isSubmitting: false,
      });
      dispatch(GetDetails(navigate, location?.state));
    } catch (error) {
      toast.error(
        error?.message === "Missing verified login tokens."
          ? "Login verification succeeded, but no session tokens were returned."
          :
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          "Invalid authenticator code"
      );
      setLoginTotpChallenge((prev) => ({
        ...prev,
        isSubmitting: false,
      }));
    }
  };

  useEffect(() => {
    let loginTimeout;
    if (location?.state?.from) {
      loginTimeout = setTimeout(() => {
        dispatch(toggleAuthModal());
      }, 1500);
    }
    return () => {
      loginTimeout && clearTimeout(loginTimeout);
    };
    // NOTE: Run effect once on component mount, please
    // recheck dependencies if effect is updated.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto focus Email token
  const autoFocusHandler = (event) => {
    const element = event.target;
    const nextSibling = element.nextElementSibling;
    nextSibling ? nextSibling.focus() : element.blur();
  };

  // Verify Email Refs
  const ref1 = useRef();
  const ref2 = useRef();
  const ref3 = useRef();
  const ref4 = useRef();
  const ref5 = useRef();
  const ref6 = useRef();

  const resendCode = async () => {
    try {
      toast.success("Sending new code. Please wait...");
      const { data: res } = await base.post("core/resend-otp/", {
        user_id: id,
      });
      if (res?.status === "success") {
        toast.success("New activation code has been sent to your email");
      }
    } catch (error) {
      toast.warn("Sorry, could not send code");
      return;
    }
  };

  const verifyAccount = async () => {
    const code =
      ref1.current?.value +
      ref2.current?.value +
      ref3.current?.value +
      ref4.current?.value +
      ref5.current?.value +
      ref6.current?.value;
    if (code === "") {
      toast.warn("Invalid. Please provide activation code");
      return;
    }
    if (code?.length < 6) {
      toast.warn("Invalid activation code. Please check your code");
      return;
    }
    try {
      setIsLoading(true);
      const { data: res } = await base.post("core/verify-email/", {
        user_id: id,
        otp: +code,
      });
      if (res?.status === "success") {
        props.GetDetails();
        toast.success(res?.message);
        dispatch(toggleAuthModal());
      }
    } catch (error) {
      if (error?.response?.data) {
        toast.warn(error?.response?.data?.detail);
      } else {
        toast.warn("Sorry, email verification failed. Please try again");
      }
      return;
    } finally {
      setIsLoading(false);
    }
  };

  // Use the centralized dark mode hook instead of local logic
  // const { isDark } = useDarkMode();

  // Remove the old dark mode useEffects and replace with this simpler one
  useEffect(() => {
    // The useDarkMode hook already handles document styling
    // We just need to listen for cross-tab changes
    if (typeof window === "undefined") return;

    const onStorage = (e) => {
      if (e.key === "bpoh-new-dashboard-theme" && e.newValue) {
        // The useDarkMode hook will handle the actual styling
        const event = new CustomEvent("bpoh-theme-changed", {
          detail: e.newValue,
        });
        window.dispatchEvent(event);
      }
    };

    const onCustom = (e) => {
      // Custom event handler for theme changes
      const mode = e?.detail;
      if (mode === "dark" || mode === "light") {
        // The useDarkMode hook will handle the actual styling
      }
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener("bpoh-theme-changed", onCustom);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("bpoh-theme-changed", onCustom);
    };
  }, []);

  return (
    <>
      {loading && <Spinner />}

      {/* Authenitication Modal */}
      {id && isVerified ? null : (
        <Modal
          open={isModalOpen}
          onClose={closeAuthFlow}
          style={{ minWidth: "24rem" }}
        >
          {loginTotpChallenge.required ? (
            <Box
              component="form"
              onSubmit={(event) => {
                event.preventDefault();
                verifyLoginTotp();
              }}
              padding={"2rem 1.5rem"}
              textAlign={"center"}
            >
              <Typography variant="h5" fontWeight={600} mt={1} mb={1}>
                Two-Factor Authentication
              </Typography>
              <Typography variant="body1" mb={3}>
                {loginTotpChallenge.useRecoveryCode
                  ? "Enter one of your saved recovery codes to finish signing in."
                  : "Enter the 6-digit code from your authenticator app to finish signing in."}
              </Typography>
              <TextField
                fullWidth
                autoFocus
                label={
                  loginTotpChallenge.useRecoveryCode
                    ? "Recovery code"
                    : "Authentication code"
                }
                placeholder={
                  loginTotpChallenge.useRecoveryCode ? "ABCD-EFGH" : "123456"
                }
                value={loginTotpChallenge.code}
                onChange={(event) =>
                  setLoginTotpChallenge((prev) => ({
                    ...prev,
                    code: loginTotpChallenge.useRecoveryCode
                      ? event.target.value.toUpperCase()
                      : event.target.value.replace(/\D/g, "").slice(0, 6),
                  }))
                }
                inputProps={{
                  inputMode: loginTotpChallenge.useRecoveryCode
                    ? "text"
                    : "numeric",
                  pattern: loginTotpChallenge.useRecoveryCode
                    ? undefined
                    : "[0-9]*",
                  maxLength: loginTotpChallenge.useRecoveryCode ? 32 : 6,
                }}
                sx={{ mb: 2.5 }}
              />
              <Button
                variant="text"
                fullWidth
                sx={{ textTransform: "none", marginBottom: "1rem" }}
                onClick={() =>
                  setLoginTotpChallenge((prev) => ({
                    ...prev,
                    useRecoveryCode: !prev.useRecoveryCode,
                    code: "",
                  }))
                }
              >
                {loginTotpChallenge.useRecoveryCode
                  ? "Use authenticator code instead"
                  : "Forgot your authenticator? Use a recovery code"}
              </Button>

              <LoadingButton
                type="submit"
                loading={loginTotpChallenge.isSubmitting}
                variant="contained"
                disableElevation
                color="secondary"
                fullWidth
                size="large"
                sx={{ textTransform: "none", marginBottom: "1rem" }}
                onClick={verifyLoginTotp}
              >
                Verify and continue
              </LoadingButton>
              <Button
                variant="text"
                fullWidth
                sx={{ textTransform: "none" }}
                onClick={closeAuthFlow}
              >
                Cancel
              </Button>
            </Box>
          ) : id && !isVerified ? (
            <Box padding={"2rem 1.5rem"} textAlign={"center"}>
              <img src={EMAIL} width={"22%"} alt="email" />
              <Typography variant="h5" fontWeight={500} mt={1} mb={1}>
                Verify Email
              </Typography>
              <Typography variant="body1" mb={2}>
                To start using bpohomes.com, we have sent a verification token
                to your email (<b>{email}</b>)
              </Typography>
              <Stack
                direction={"row"}
                gap={".6rem"}
                my={3}
                width={"100%"}
                mx={"auto"}
              >
                {new Array(6).fill(0).map((el, i) => {
                  return (
                    <input
                      key={i}
                      autoFocus={i === 0 ? true : false}
                      ref={
                        i === 0
                          ? ref1
                          : i === 1
                            ? ref2
                            : i === 2
                              ? ref3
                              : i === 3
                                ? ref4
                                : i === 4
                                  ? ref5
                                  : ref6
                      }
                      type="text"
                      maxLength={1}
                      onChange={(e) => autoFocusHandler(e)}
                      style={{
                        textAlign: "center",
                        height: "4rem",
                        fontSize: "2rem",
                        width: "100%",
                        borderRadius: "3px",
                        border: "1px solid lightgrey",
                      }}
                    />
                  );
                })}
              </Stack>
              <LoadingButton
                loading={isLoading}
                variant="contained"
                disableElevation
                color="secondary"
                fullWidth
                size="large"
                sx={{ textTransform: "none", marginBottom: "1rem" }}
                onClick={verifyAccount}
              >
                Verify Account
              </LoadingButton>
              <Typography
                variant="body2"
                onClick={resendCode}
                color={"secondary"}
                fontWeight={400}
                sx={{ cursor: "pointer", textDecoration: "underline" }}
              >
                Didn't receive email. Resend
              </Typography>
            </Box>
          ) : (
            <AuthContent
              closeModal={closeAuthFlow}
              accountDetails={accountDetails}
              onSubmit={onSubmit}
              setAccountDetails={(name, value) =>
                setAccountDetails({
                  ...accountDetails,
                  [name]: value,
                })
              }
              redirect={location?.state}
            />
          )}
        </Modal>
      )}
      <nav className="navbar">
        <NavLink
          className="mob_nav_element"
          to=""
          style={{ marginLeft: "5px" }}
        >
          {menuState ? (
            <CloseIcon onClick={() => toggleMenu(menuState)} />
          ) : (
            <Bars onClick={() => toggleMenu(menuState)} />
          )}
        </NavLink>

        {pathname !== "/" ? (
          <NavLink to="/">
            <img src={Logo} alt="logo" className="logo" />
          </NavLink>
        ) : (
          <img
            src={Logo}
            alt="logo"
            className="logo"
          // style={{ boxSizing: "content-box", height: "38px" }}
          />
        )}
        <ul className="list">
          <NavLink
            to="/order-bpo-report"
            className={({ isActive }) =>
              isActive ? "list-active link_font" : "link_font"
            }
          >
            Order BPO Report
          </NavLink>
          <a href={indProfLink} target="_blank" rel="noreferrer">
            <li className="link_font">Industry Professionals</li>
          </a>
          {/* <NavLink to='/analyze-deal' className={({ isActive }) => (isActive ? 'list-active' : '')}>
						<li className='link_font'>Deal Analyzer</li>
					</NavLink> */}
          <NavLink
            to="analyze-deal"
            className={({ isActive }) => (isActive ? "list-active" : "")}
          >
            <li className="link_font">Deal Analyzer</li>
          </NavLink>
          <NavLink
            to="/agent-finder"
            className={({ isActive }) => (isActive ? "list-active" : "")}
          >
            <li className="link_font">Agent Finder</li>
          </NavLink>
          {/* <NavLink to='/advertise' className={({ isActive }) => (isActive ? 'list-active link_font' : 'link_font')}>
						Advertise
					</NavLink> */}
        </ul>

        {!token?.access ? (
          <>
            <ul className="list">
              <li className="link_font" onClick={openLogin}>
                Login
              </li>
              <li
                className={`${"link_font"}`}
                onClick={openNewAccount}
                style={{
                  background: "#004176",
                  color: "#fff",
                  padding: ".6rem 1.2rem",
                  borderRadius: "50px",
                  marginLeft: "-1rem",
                }}
              >
                Sign up
              </li>
            </ul>
            <p
              onClick={openLogin}
              className="mob_nav_element"
              style={{ marginRight: "10px" }}
            >
              Sign In
            </p>
          </>
        ) : (
          <ul className="list logged" style={{ display: "block" }}>
            <UserMenuOptions name={`${first_name} ${last_name}`} />
          </ul>
        )}
      </nav>
      <div className={menuState ? "dropdownnav " : "navtoggle"}>
        <div className="dropdownnav_list">
          <div className="nav_links_wrapper">
            <NavLink
              to="/order-bpo-report"
              className={({ isActive }) =>
                isActive ? "list-active nav_link" : "nav_link"
              }
              onClick={() => {
                setMenuState(false);
              }}
            >
              <p>Order BPO Report</p>
            </NavLink>
            <NavLink
              to="/agent-finder"
              className={({ isActive }) =>
                isActive ? "list-active nav_link" : "nav_link"
              }
              onClick={() => {
                setMenuState(false);
              }}
            >
              <p>Agent Finder</p>
            </NavLink>
            <a
              href={indProfLink}
              target="_blank"
              rel="noreferrer"
              className="nav_link"
              onClick={() => {
                setMenuState(false);
              }}
            >
              <p>Industry Professionals</p>
            </a>
            <NavLink
              to="/advertise"
              className={({ isActive }) =>
                isActive ? "list-active nav_link" : "nav_link"
              }
              onClick={() => {
                setMenuState(false);
              }}
            >
              <p>Advertise</p>
            </NavLink>
          </div>
        </div>
      </div>
    </>
  );
}

const mapStateToProps = (state) => state;
export default connect(mapStateToProps, { GetDetails })(Navbar);
